/**
 * Верхняя панель: баланс, логотип 3CupsGames, кнопки управления
 */

import * as PIXI from 'pixi.js';
import { TextStyle } from 'pixi.js';
import { BalanceDisplay } from './BalanceDisplay';
import { Button } from './Button';
import { COLORS } from '@/config/colors';
import { getSoundEnabled } from '@/utils/storage';
import type { Lang } from '@/utils/storage';
import { SoundManager } from '@/audio/SoundManager';

export class Header extends PIXI.Container {
  private balanceDisplay: BalanceDisplay;
  private helpBtn: Button;
  private soundBtn: Button;
  private fullscreenBtn: Button;
  private menuBtn: Button;
  private demoModeText: PIXI.Text;
  private soundEnabled = getSoundEnabled();
  private readonly forceMuted = SoundManager.get().isForcedMuted();
  private onHelpClick?: () => void;
  private onMenuClick?: () => void;

  constructor() {
    super();
    // Sync with SoundManager (single source of truth)
    this.soundEnabled = SoundManager.get().isEnabled();
    this.balanceDisplay = new BalanceDisplay();
    this.balanceDisplay.x = 20;
    this.balanceDisplay.y = 30;
    this.addChild(this.balanceDisplay);

    const logoY = 30;
    const btnSize = 40;
    const btnSpacing = 50;
    let x = -20;

    this.helpBtn = new Button({
      width: btnSize,
      height: btnSize,
      radius: btnSize / 2,
      fillColor: COLORS.buttonSecondary,
      text: '?',
      fontSize: 20,
    });
    x -= btnSpacing;
    this.helpBtn.x = x;
    this.helpBtn.y = logoY;
    this.helpBtn.on('pointerdown', () => this.onHelpClick?.());
    this.addChild(this.helpBtn);

    this.soundBtn = new Button({
      width: btnSize,
      height: btnSize,
      radius: btnSize / 2,
      fillColor: COLORS.buttonSecondary,
      text: this.soundEnabled ? '🔊' : '🔇',
      fontSize: 20,
    });
    x -= btnSpacing;
    this.soundBtn.x = x;
    this.soundBtn.y = logoY;
    this.soundBtn.on('pointerdown', () => {
      SoundManager.get().playSfx('ui_click');
      this.toggleSound();
    });
    this.addChild(this.soundBtn);
    if (this.forceMuted) {
      this.soundEnabled = false;
      this.soundBtn.setText('🔇');
      this.soundBtn.setEnabled(false);
    }

    this.fullscreenBtn = new Button({
      width: btnSize,
      height: btnSize,
      radius: btnSize / 2,
      fillColor: COLORS.buttonSecondary,
      text: '⛶',
      fontSize: 20,
    });
    x -= btnSpacing;
    this.fullscreenBtn.x = x;
    this.fullscreenBtn.y = logoY;
    this.fullscreenBtn.on('pointerdown', () => {
      SoundManager.get().playSfx('ui_click');
      this.toggleFullscreen();
    });
    this.addChild(this.fullscreenBtn);

    this.menuBtn = new Button({
      width: btnSize,
      height: btnSize,
      radius: btnSize / 2,
      fillColor: COLORS.buttonSecondary,
      text: '☰',
      fontSize: 20,
    });
    x -= btnSpacing;
    this.menuBtn.x = x;
    this.menuBtn.y = logoY;
    this.menuBtn.on('pointerdown', () => this.onMenuClick?.());
    this.addChild(this.menuBtn);

    this.demoModeText = new PIXI.Text(
      'DEMO MODE',
      new TextStyle({
        fontFamily: '"Jost"',
        fontSize: 12,
        fill: COLORS.demoMode,
        fontWeight: 'bold',
      })
    );
    this.demoModeText.anchor.set(0.5);
    this.demoModeText.rotation = -Math.PI / 2;
    this.demoModeText.x = -10;
    this.demoModeText.y = 200;
    this.addChild(this.demoModeText);
  }

  setBalance(balance: number): void {
    this.balanceDisplay.setBalance(balance);
  }

  setLang(lang: Lang): void {
    this.balanceDisplay.setLang(lang);
  }

  setOnHelpClick(callback: () => void): void {
    this.onHelpClick = callback;
  }

  setOnMenuClick(callback: () => void): void {
    this.onMenuClick = callback;
  }

  getBalanceTarget(): { x: number; y: number } {
    return {
      x: this.balanceDisplay.x + this.balanceDisplay.width,
      y: this.balanceDisplay.y,
    };
  }

  private toggleSound(): void {
    if (this.forceMuted) return;
    const audio = SoundManager.get();
    if (this.soundEnabled) {
      this.soundEnabled = false;
      audio.setEnabled(false);
    } else {
      this.soundEnabled = true;
      audio.setEnabled(true);
    }
    this.soundBtn.setText(this.soundEnabled ? '🔊' : '🔇');
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  }

  resize(width: number, mobile?: boolean): void {
    const btnSize = 40;
    const btnSpacing = 50;
    const rightPadding = 24;

    this.balanceDisplay.scale.set(mobile ? 2 : 1);
    this.balanceDisplay.x = 20;
    this.balanceDisplay.y = mobile ? 40 : 30;

    this.soundBtn.visible = !this.forceMuted;

    let x = width - rightPadding - btnSize;
    for (const btn of [this.helpBtn, this.soundBtn, this.fullscreenBtn, this.menuBtn]) {
      if (!btn.visible) continue;
      btn.scale.set(1);
      btn.x = x;
      btn.y = 30;
      x -= btnSpacing;
    }
    this.demoModeText.x = width - 10;
  }
}
